import {flavorDescriptions, colors, strokeWidth} from '../constants';

import {
  createPoint,
  buildGeometryInstance,
  isSameCoordinatePair,
  getLayerForFlavoredEntityType,
  handleAddCorrectnessIcon,
  hasCoords
} from './entities.utils';

const circle = {
  /**
   * @example
   * const entity = {
   *   type: 'circle',
   *   elements: {
   *     center: [0, 0],
   *     radiusPoint: [5, 5]
   *   },
   *   flavor: 'studentGuess'
   * };
   */

  create({board, entity, onClick}) {
    const {
      flavor,
      elements: {center, radiusPoint}
    } = entity;

    const centerElement = createPoint({
      board,
      coords: center,
      flavor
    });
    const radiusPointElement = createPoint({
      board,
      coords: radiusPoint,
      flavor
    });

    const {color} = flavorDescriptions[flavor];

    const circle = board.create('circle', [centerElement, radiusPointElement], {
      highlightStrokeColor: color,
      highlightStrokeWidth: strokeWidth,
      strokeColor: color,
      strokeWidth,
      layer: getLayerForFlavoredEntityType('circle', flavor),
      tabindex: null
    });

    const correctnessIcon = handleAddCorrectnessIcon({board, flavor, coords: center});

    return buildGeometryInstance({
      board,
      constituentElements: [centerElement, radiusPointElement, circle],
      entity,
      geometryElement: circle,
      onClick,
      correctnessIcon
    });
  },
  createPartial({board, partialInstance, coords, flavor}) {
    if (!partialInstance) {
      return {
        entity: {
          type: 'circle',
          elements: {
            center: null,
            radiusPoint: null
          },
          flavor
        },
        geometryElements: [],
        done: false,
        remove() {
          return null;
        }
      };
    }

    const {
      entity: {
        elements,
        elements: {center}
      },
      geometryElements
    } = partialInstance;

    const isValid = !hasCoords(elements, coords);

    if (!isValid) return null;

    const nextPoint = createPoint({board, coords, flavor});

    const nextGeometryElements = [...geometryElements, nextPoint];

    const centerCoords = center || coords;

    return {
      entity: {
        type: 'circle',
        elements: {
          center: centerCoords,
          radiusPoint: center ? coords : null
        },
        flavor
      },
      geometryElements: nextGeometryElements,
      done: center !== null,
      instructions: 'Place a second point to define the radius.',
      handleCursorMove(cursorCoords) {
        if (board.elementsByName.previewCircle) {
          board.removeAncestors(board.elementsByName.previewCircle);
        }

        if (!centerCoords || !cursorCoords || isSameCoordinatePair(centerCoords, cursorCoords)) {
          return;
        }

        const radiusPoint = createPoint({
          board,
          color: colors.preview,
          coords: cursorCoords
        });

        board.create('circle', [centerCoords, radiusPoint], {
          name: 'previewCircle',
          strokeWidth,
          highlightStrokeWidth: strokeWidth,
          strokeColor: colors.preview,
          highlightStrokeColor: colors.preview,
          dash: 3,
          tabindex: null
        });
      },
      remove() {
        nextGeometryElements.forEach((element) => board.removeAncestors(element));
        return null;
      }
    };
  }
};

export default circle;
